import {
  PanelToGenesMappingDataType,
  SearchMode,
} from '@/app/[locale]/gene-coverage-browser/_types';
import { useClickAway } from '@uidotdev/usehooks';
import { useMemo, useState } from 'react';

/**
 * 향상된 검색 드롭다운 기능을 관리하는 커스텀 훅
 *
 * 주요 기능:
 * 1. 검색 모드에 따른 드롭다운 표시 여부 관리
 * 2. 외부 클릭 감지를 통한 드롭다운 닫기
 * 3. 아이템 선택 및 입력 필드 포커스 관리
 * 4. 패널 모드일 때 필터링된 패널 목록 제공
 * 5. 패널 모드에서 항목 선택 시 바로 검색 수행
 *
 * @param {string} inputValue - 현재 입력된 검색어
 * @param {Function} setInputValue - 입력값 설정 함수
 * @param {('Genes'|'Panel')} searchMode - 현재 검색 모드 (유전자 또는 패널)
 * @param {PanelToGenesMappingDataType} panelToGenesMappingData - 패널 이름과 해당 유전자 목록 매핑 데이터
 * @param {Function} onSearch - 검색 실행 시 호출될 콜백 함수 (원본 입력값을 인자로 받음)
 * @returns {Object} 드롭다운 관련 상태와 함수들
 */
export const useGenePanelDropdown = (
  inputValue: string,
  setInputValue: (value: string) => void,
  searchMode: SearchMode,
  panelToGenesMappingData: PanelToGenesMappingDataType,
  onSearch: (originalInput: string) => void,
) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  // 외부 클릭 감지
  const inputRef = useClickAway<HTMLDivElement>(() => {
    setIsDropdownVisible(false);
  });

  // panelToGenesMappingData의 키 목록을 메모이제이션
  const panelNames = useMemo(() => {
    return Object.keys(panelToGenesMappingData);
  }, [panelToGenesMappingData]);

  // Panel 모드일때 필터링된 패널 목록 (useMemo로 최적화)
  const genesIncludedInPanel = useMemo(() => {
    if (searchMode === 'Panel' && inputValue) {
      return panelNames.filter(panel =>
        panel.toLowerCase().includes(inputValue.toLowerCase()),
      );
    }
    return [];
  }, [searchMode, inputValue, panelNames]);

  // 아이템 선택 핸들러
  const handleItemSelect = (item: string) => {
    setInputValue(item);
    setIsDropdownVisible(false);

    // 패널 모드에서 항목 선택 시 바로 검색 수행 (원본 패널 이름 전달)
    if (searchMode === 'Panel' && onSearch && panelToGenesMappingData[item]) {
      onSearch(item);
    }
  };

  // 입력 변경 시 드롭다운 표시 설정
  const handleDropdownVisibility = () => {
    if (searchMode === 'Panel') {
      setIsDropdownVisible(true);
    }
  };

  return {
    isDropdownVisible: searchMode === 'Panel' ? isDropdownVisible : false,
    genesIncludedInPanel,
    inputRef,
    handleItemSelect,
    handleDropdownVisibility,
  };
};
